import React from 'react';
import useSEO from '../HomePage/useSEO';
import Header from '../Header&Footer/Header';
import Footer from '../Header&Footer/Footer';
import { Button } from '@/Components/ui/button';
import {
  UserCheck,
  BarChart3,
  ShieldCheck,
  MessageSquare,
  Lightbulb,
  Crown,
  Search,
  TrendingUp,
  Target,
  Brain,
  Sparkles,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

/**
 * Screening Assessment page — restyled to match the SearchJob.ai landing
 * theme: cream background, forest-green primary, gradient heading accents
 * (primary → emerald → teal), eyebrow pills, rounded-3xl cards, pill CTAs,
 * and dark-ink bands for the stats + final CTA.
 *
 * All copy, data and links are preserved from the original page; only the
 * presentation was brought in line with the landing page.
 */

const INK = 'hsl(164 33% 7%)'; // brand dark-ink surface (Stats / CTA bands)

export default function ScreeningAssessment() {
  useSEO({
    title:
      'AI Resume Screening & Assessment — Score & Rank Candidates | SearchJob.ai',
    description:
      'AI screens and scores every resume against your job description, ranks candidates by fit, and routes the best to assessment — in minutes, not weeks.',
    canonical: 'https://searchjob.ai/hirewithus/screening-assessment',
    ogImage: 'https://searchjob.ai/og-image.png',
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-16">
        {/* ═════════════ HERO ═════════════ */}
        <section className="relative overflow-hidden bg-background isolate">
          {/* Gradient mesh + grid, matching the landing hero */}
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl" />
            <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
          </div>
          <div
            className="absolute inset-0 -z-10 opacity-[0.12] pointer-events-none"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '44px 44px',
              color: 'hsl(var(--foreground) / 0.1)',
              maskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
            }}
          />

          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              AI-Powered Screening Assessment Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em] text-foreground leading-[1.08] mb-6">
              Identify Top Talent with
              <br />
              <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                Intelligent Pre-Screening
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
              Our AI-powered screening assessment platform transforms the
              initial candidate evaluation process with comprehensive, unbiased
              and efficient screening across multiple dimensions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                asChild
                size="lg"
                className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25"
              >
                <a href="/hirewithus/login">
                  Start Screening
                  <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="h-12 px-8 text-base font-semibold !rounded-full bg-card/70 backdrop-blur-md"
              >
                <a href="/hirewithus/contact">Contact Us</a>
              </Button>
            </div>
          </div>
        </section>

        {/* ═════════════ OVERVIEW ═════════════ */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Eyebrow>Overview</Eyebrow>
              <SectionHeading plain="Comprehensive candidate evaluation," accent="powered by AI." />
              <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                Our AI-powered screening assessment platform transforms the initial candidate evaluation process.
                Built with advanced machine learning algorithms, it provides comprehensive, unbiased, and efficient
                screening of candidates across multiple dimensions before they reach your hiring team.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                From behavioral assessments to skill evaluations and cultural fit analysis, our platform ensures
                that only the most qualified candidates progress through your hiring pipeline, saving valuable
                time and resources while improving hiring quality.
              </p>
              <Button
                asChild
                size="lg"
                className="!bg-primary !text-primary-foreground hover:!bg-primary/90 !rounded-full h-12 px-8 font-semibold"
              >
                <a href="/hirewithus/login">Start Screening</a>
              </Button>
            </div>
            <ImageCard
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="AI-powered screening assessment interface"
            />
          </div>
        </Section>

        {/* ═════════════ KEY FEATURES ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2071&q=80"
              alt="Comprehensive candidate screening"
              className="lg:order-2"
            />
            <div className="lg:order-1">
              <Eyebrow>What you get</Eyebrow>
              <SectionHeading plain="Key features that" accent="screen smarter." />
              <CheckList
                items={[
                  'AI-Powered Behavioral Analysis - Intelligent evaluation of personality traits and work style preferences',
                  'Multi-Dimensional Assessment - Comprehensive evaluation across skills, experience, and cultural fit',
                  'Automated Resume Parsing - Intelligent extraction and analysis of candidate information',
                  'Customizable Screening Criteria - Role-specific assessment parameters and evaluation metrics',
                  'Bias Detection & Elimination - Advanced algorithms to ensure fair and objective evaluation',
                  'Real-Time Scoring & Ranking - Instant candidate ranking based on multiple assessment criteria',
                ]}
              />
            </div>
          </div>
        </Section>

        {/* ═════════════ CORE CAPABILITIES ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Core capabilities</Eyebrow>
            <SectionHeading center plain="Essential screening tools for" accent="comprehensive evaluation." />
            <p className="text-lg text-muted-foreground leading-relaxed">
              Essential screening tools for comprehensive candidate evaluation
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: UserCheck, title: 'Behavioral', desc: 'Personality & cultural fit' },
              { icon: BarChart3, title: 'Skills Gap', desc: 'Technical & soft skills' },
              { icon: ShieldCheck, title: 'Experience', desc: 'Background validation' },
              { icon: MessageSquare, title: 'Communication', desc: 'Verbal & written skills' },
              { icon: Lightbulb, title: 'Problem Solving', desc: 'Critical thinking' },
              { icon: Crown, title: 'Leadership', desc: 'Management potential' },
            ].map((capability, index) => {
              const Icon = capability.icon;
              return (
                <div
                  key={index}
                  className="bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300 text-center"
                >
                  <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/25 mx-auto mb-4">
                    <Icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-1.5">
                    {capability.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-snug">
                    {capability.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </Section>

        {/* ═════════════ WHY CHOOSE OUR PLATFORM ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1516116216624-53e697fedbea?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2128&q=80"
              alt="AI technology innovation"
              className="lg:order-2"
            />
            <div className="lg:order-1">
              <Eyebrow>Why choose us</Eyebrow>
              <SectionHeading plain="Why choose" accent="our platform?" />
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Traditional screening methods often rely on manual review processes that are time-consuming,
                inconsistent, and prone to human bias. Our AI-powered solution addresses these challenges with
                intelligent automation and data-driven insights.
              </p>
              <div className="space-y-5">
                {[
                  {
                    title: 'Eliminate Manual Screening',
                    desc: 'Automated assessment processes that reduce screening time from hours to minutes',
                  },
                  {
                    title: 'Consistent & Unbiased Evaluation',
                    desc: 'AI-driven assessment ensures fair and consistent evaluation across all candidates',
                  },
                  {
                    title: 'Data-Driven Insights',
                    desc: 'Comprehensive analytics and reporting for better hiring decisions and process optimization',
                  },
                ].map((item, i) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-emerald-800 text-primary-foreground flex items-center justify-center font-black text-lg shadow-lg shadow-primary/25">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* ═════════════ STATS BAND (dark ink) ═════════════ */}
        <section className="relative py-14 lg:py-20 overflow-hidden" style={{ backgroundColor: INK }}>
          <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-2/3 h-1/2 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
              {[
                { v: '75%', l: 'Faster Screening Process' },
                { v: '3X', l: 'More Candidates Evaluated' },
                { v: '50%', l: 'Reduction in Screening Costs' },
              ].map((s) => (
                <div key={s.l}>
                  <div className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    {s.v}
                  </div>
                  <div className="mt-2 text-sm text-white/70">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ═════════════ ASSESSMENT TYPES & FORMATS ═════════════ */}
        <Section>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow center>Assessment library</Eyebrow>
            <SectionHeading center plain="Assessment types &" accent="formats." />
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our platform supports a comprehensive range of assessment types and formats to meet
              your specific screening requirements across different roles and industries.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {[
              {
                title: 'Behavioral Assessments',
                desc: 'Personality tests, work style preferences, cultural fit evaluation, and situational judgment scenarios',
              },
              {
                title: 'Skills Assessments',
                desc: 'Technical skills tests, soft skills evaluation, language proficiency, and domain-specific knowledge',
              },
              {
                title: 'Video Interviews',
                desc: 'AI-powered video screening with facial expression analysis and communication skills evaluation',
              },
              {
                title: 'Communication Assessments',
                desc: 'Verbal communication skills, presentation abilities, active listening, and interpersonal interaction evaluation',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-card border border-border/70 rounded-3xl p-6 shadow-sm hover:shadow-[0_12px_48px_-12px_hsl(var(--primary)/0.22)] hover:-translate-y-1 transition-all duration-300"
              >
                <h3 className="text-base font-bold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* ═════════════ ADVANCED AI CAPABILITIES ═════════════ */}
        <Section muted>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <ImageCard
              src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="AI-powered screening technology"
              className="lg:order-2"
            />
            <div className="lg:order-1">
              <Eyebrow>Advanced AI</Eyebrow>
              <SectionHeading plain="Advanced AI" accent="capabilities." />
              <div className="space-y-5">
                {[
                  {
                    icon: Search,
                    title: 'Intelligent Pattern Recognition',
                    desc: 'AI-powered analysis of candidate responses to identify patterns and predict job performance',
                  },
                  {
                    icon: TrendingUp,
                    title: 'Predictive Analytics',
                    desc: 'Machine learning models that predict candidate success based on assessment performance and historical data',
                  },
                  {
                    icon: Target,
                    title: 'Adaptive Questioning',
                    desc: 'Dynamic assessment adjustment based on candidate responses for optimal evaluation accuracy',
                  },
                  {
                    icon: Brain,
                    title: 'Natural Language Processing',
                    desc: 'Advanced NLP capabilities for understanding and evaluating written and verbal responses',
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-primary flex items-center justify-center shadow-lg shadow-primary/25">
                        <Icon className="w-5 h-5 text-primary-foreground" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-foreground mb-1.5 leading-snug">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Section>

        {/* ═════════════ FINAL CTA (dark ink) ═════════════ */}
        <section className="relative py-16 lg:py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-[2.5rem] p-10 lg:p-16 text-white shadow-2xl text-center"
              style={{ backgroundColor: INK }}
            >
              <div className="absolute top-[-30%] right-[-10%] w-[60%] h-[80%] bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />
              <div className="absolute bottom-[-30%] left-[-10%] w-[50%] h-[70%] bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />
              <div className="relative max-w-2xl mx-auto">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-5 leading-tight">
                  Ready to Transform Your
                  <br />
                  <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">
                    Screening Process?
                  </span>
                </h2>
                <p className="text-lg text-white/70 leading-relaxed mb-8">
                  Experience the future of candidate screening with AI-powered precision, automated evaluation, and comprehensive insights that help you identify the best talent faster and more efficiently.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    asChild
                    size="lg"
                    className="!bg-white !text-foreground hover:!bg-white/90 h-12 px-8 text-base font-semibold group shadow-xl !rounded-full"
                  >
                    <a href="/hirewithus/contact">
                      Contact Us
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </a>
                  </Button>
                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="!border-white/30 !bg-transparent !text-white hover:!bg-white/10 h-12 px-8 text-base font-semibold !rounded-full"
                  >
                    <a href="/hirewithus/login">Hire With Us</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  Small shared bits — mirror the landing page's building blocks    */
/* ─────────────────────────────────────────────────────────────── */
function Section({ children, muted = false }) {
  return (
    <section
      className={`relative py-14 lg:py-20 overflow-hidden ${
        muted ? 'bg-muted/30' : 'bg-background'
      }`}
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

function Eyebrow({ children, center = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-4 ${
        center ? 'mx-auto' : ''
      }`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      {children}
    </div>
  );
}

function SectionHeading({ plain, accent, center = false }) {
  return (
    <h2
      className={`text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground leading-[1.1] mb-5 ${
        center ? 'text-center' : ''
      }`}
    >
      {plain}{' '}
      <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
        {accent}
      </span>
    </h2>
  );
}

function CheckList({ items }) {
  return (
    <ul className="space-y-3">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3">
          <span className="shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-primary" strokeWidth={2.5} />
          </span>
          <span className="text-base text-foreground/85 leading-relaxed">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ImageCard({ src, alt, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <div className="absolute -inset-4 bg-gradient-to-br from-primary/15 via-emerald-500/10 to-teal-500/15 blur-2xl rounded-full -z-10" />
      <img
        src={src}
        alt={alt}
        className="w-full max-w-[540px] mx-auto h-auto rounded-3xl border border-border shadow-2xl ring-1 ring-foreground/5"
      />
    </div>
  );
}
